import type { TimelineEntry } from '@compass/client/types/case_record';
import type { User } from '@compass/client/types/user';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { caseRecordService, userService } from '@/services/api';
import { useStore } from '@/store/store';

interface UseCaseTimelineOptions {
  caseRecordId: string;
  enabled?: boolean;
}

const PAGE_SIZE = 50;

/**
 * Collect every user id an entry can reference: the actor, the assignment
 * change's from/to users (case.assigned), and the user assigned at creation
 * (case.created). The detail arms are event-type-specific, so reading them
 * unconditionally is safe — absent arms just yield undefined and are filtered.
 */
function collectEntryUserIds(entry: TimelineEntry): Array<string | undefined> {
  const detail = entry.detail;
  return [
    entry.actor_id,
    detail?.assigned?.to_user_id,
    detail?.assigned?.from_user_id,
    detail?.created?.assigned_user_id,
  ];
}

/**
 * Fetch the activity timeline for a case record (case.created, case.state.changed,
 * case.assigned, etc.) with cursor-based infinite scroll pagination.
 */
export function useCaseTimeline(options: UseCaseTimelineOptions) {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  const query = useInfiniteQuery({
    queryKey: ['caseTimeline', options.caseRecordId, selectedOrganizationId, selectedProgramId],
    queryFn: ({ pageParam }) =>
      caseRecordService.getCaseRecordTimeline({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_record_id: options.caseRecordId,
        limit: PAGE_SIZE,
        after: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => lastPage.next_cursor || undefined,
    enabled:
      (options.enabled ?? true) &&
      !!options.caseRecordId &&
      !!selectedOrganizationId &&
      !!selectedProgramId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const entries: TimelineEntry[] = useMemo(
    () => query.data?.pages.flatMap(page => page.entries || []) ?? [],
    [query.data?.pages],
  );

  const uniqueUserIds = useMemo(() => {
    const ids = new Set<string>();
    for (const entry of entries) {
      for (const id of collectEntryUserIds(entry)) {
        if (id) ids.add(id);
      }
    }
    return [...ids];
  }, [entries]);

  const usersQuery = useQuery({
    queryKey: ['users', 'byIds', uniqueUserIds],
    queryFn: () => userService.lookupUsers({ user_ids: uniqueUserIds }),
    enabled: uniqueUserIds.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const usersMap = useMemo(() => {
    const map = new Map<string, User>();
    if (usersQuery.data?.users) {
      for (const user of usersQuery.data.users) {
        if (user.id) {
          map.set(user.id, { id: user.id, email: user.email, name: user.name });
        }
      }
    }
    return map;
  }, [usersQuery.data?.users]);

  return {
    entries,
    isLoading: query.isLoading,
    error: query.error,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    usersMap,
    isLoadingUsers: usersQuery.isLoading && uniqueUserIds.length > 0,
  };
}
